import { ActivityIndicator, FlatList, StyleSheet, View, useWindowDimensions } from 'react-native';
import { useTheme } from '@react-navigation/native';
import { showMessage } from 'react-native-flash-message';
import { useTranslation } from 'react-i18next';
import { createCommonStyles } from '../../styles/commonStyles';
import { useContext, useEffect, useRef, useState } from 'react';
import { Card } from 'react-native-paper';
import { useForm } from 'react-hook-form';
import CustomControlledDropdown from '../../components/ControlledComponents/CustomControlledDropdown';
import CustomCalendar from '../../components/CustomCalendar/CustomCalendar';
import CustomLabel from '../../components/CustomLabel/CustomLabel';
import CustomButton from '../../components/CustomButton';
import CustomAlert from '../../components/CustomAlert/CustomAlert';
import ShiftsRosterScreen from './ShiftsRosterScreen';
import { useShifts } from '../../hooks/UseShifts';
import { useCompany } from '../../hooks/useCompany';
import { useHRAdminEmployees } from '../../hooks/useHRAdminEmployees';
import { HRAdminCompanyContext } from '../../context/HRAdminCompanyContext';
import Ionicons from "@react-native-vector-icons/ionicons/static";

const ShiftsSchedulePeriodsScreen = () => {
	const { colors } = useTheme();
	const { width } = useWindowDimensions();
	const { t } = useTranslation();
	const common = createCommonStyles(colors);
	const styles = makeStyles(colors);
	const { control, clearErrors, getValues, handleSubmit, setError, setValue, watch, unregister, formState: { errors } } = useForm();
	const { companyOptions, setCompanyOptions } = useContext(HRAdminCompanyContext);
	const [schedulePeriods, setSchedulePeriods] = useState([]);
	const [departmentOptionsByRow, setDepartmentOptionsByRow] = useState({});
	const [departmentLoadingByRow, setDepartmentLoadingByRow] = useState({});
	const [activeCalendar, setActiveCalendar] = useState(null);
	const [selectedRosterPeriodId, setSelectedRosterPeriodId] = useState(null);
	const [rosterHasPendingChanges, setRosterHasPendingChanges] = useState(false);
	const [calendarRange, setCalendarRange] = useState({ startDate: '', endDate: '' });
	const { getSchedulePeriods, saveSchedulePeriod, deleteSchedulePeriod, isLoading } = useShifts();
	const { getCompanies } = useCompany();
	const { getDropdownValues } = useHRAdminEmployees();
	const getDropdownValuesRef = useRef(getDropdownValues);
	getDropdownValuesRef.current = getDropdownValues;
	const getPeriodFormKey = (period) => String(period.clientKey ?? period.id);
	const mapDepartmentOptions = (departments) => (Array.isArray(departments) ? departments : []).map((department) => {
		const keys = Object.keys(department);
		return {
			value: department[keys[0]] ?? department[keys[1]],
			label: department[keys[1]] ?? department[keys[0]],
		};
	});
	const selectedRosterPeriod = schedulePeriods.find((period) => String(period.id) === selectedRosterPeriodId);
	const formValues = watch();
	const hasChanges = schedulePeriods.some((period) => {
		if (period.isNew || period.isMarkedForDelete) return true;
		if (period.status !== 'Draft') return false;

		const values = formValues?.[getPeriodFormKey(period)] || {};
		return ['company', 'dept', 'startDate', 'endDate'].some((field) => (
			(values[field] ?? '') !== (period[field] ?? '')
		));
	});

	const loadSchedulePeriods = async () => {
		const periods = await getSchedulePeriods();
		const loadedPeriods = Array.isArray(periods) ? periods : [];
		const companyCodes = [...new Set(loadedPeriods.map((period) => period.company).filter(Boolean))];
		const departmentOptionsByCompany = Object.fromEntries(await Promise.all(companyCodes.map(async (companyCode) => {
			const response = await getDropdownValuesRef.current([
				{ departmentServLineFunc: { sFilt1: companyCode, sFilt2: '', sFilt3: '' } },
			]);
			return [companyCode, mapDepartmentOptions(response?.departmentServLineFunc)];
		})));

		loadedPeriods.filter((period) => period.status === 'Draft').forEach((period) => {
			const formKey = getPeriodFormKey(period);
			['company', 'dept', 'startDate', 'endDate'].forEach((field) => {
				setValue(`${formKey}.${field}`, period[field] ?? '');
			});
		});
		setDepartmentOptionsByRow(Object.fromEntries(loadedPeriods.map((period) => (
			[getPeriodFormKey(period), departmentOptionsByCompany[period.company] || []]
		))));
		setSchedulePeriods(loadedPeriods);
	};

	const addSchedulePeriod = () => {
		const clientKey = `new-${Date.now()}`;
		['dept', 'company', 'startDate', 'endDate'].forEach((field) => {
			setValue(`${clientKey}.${field}`, '', { shouldDirty: true });
		});
		setSchedulePeriods((periods) => [{
			clientKey,
			isNew: true,
			dept: '',
			company: '',
			startDate: '',
			endDate: '',
			status: 'Draft',
		}, ...periods]);
	};

	const toggleCalendar = (clientKey, field) => {
		if (activeCalendar?.clientKey === clientKey && activeCalendar.field === field) {
			setActiveCalendar(null);
			return;
		}

		const value = getValues(`${clientKey}.${field}`);
		const currentDate = value ? String(value).split('T')[0] : new Date().toISOString().split('T')[0];
		setCalendarRange({ startDate: currentDate, endDate: currentDate });
		setActiveCalendar({ clientKey, field });
	};

	const handleCalendarChange = (range) => {
		setCalendarRange(range);
		if (activeCalendar && range?.startDate) {
			setValue(`${activeCalendar.clientKey}.${activeCalendar.field}`, range.startDate, { shouldDirty: true });
			clearErrors(`${activeCalendar.clientKey}.${activeCalendar.field}`);
			setActiveCalendar(null);
		}
	};

	useEffect(() => {
		loadSchedulePeriods();
	}, []);

	useEffect(() => {
		const subscription = watch((values, { name }) => {
			const match = /^([^\.]+)\.company$/.exec(name || '');
			if (!match) return;

			const clientKey = match[1];
			const companyCode = values?.[clientKey]?.company;
			setValue(`${clientKey}.dept`, '', { shouldDirty: true });
			setDepartmentOptionsByRow((options) => ({ ...options, [clientKey]: [] }));

			if (!companyCode) {
				setDepartmentLoadingByRow((loading) => ({ ...loading, [clientKey]: false }));
				return;
			}

			setDepartmentLoadingByRow((loading) => ({ ...loading, [clientKey]: true }));
			getDropdownValuesRef.current([
				{ departmentServLineFunc: { sFilt1: companyCode, sFilt2: '', sFilt3: '' } },
			]).then((response) => {
				if (getValues(`${clientKey}.company`) !== companyCode) return;

				const options = mapDepartmentOptions(response?.departmentServLineFunc);
				setDepartmentOptionsByRow((current) => ({ ...current, [clientKey]: options }));
			}).finally(() => {
				if (getValues(`${clientKey}.company`) === companyCode) {
					setDepartmentLoadingByRow((loading) => ({ ...loading, [clientKey]: false }));
				}
			});
		});

		return () => subscription.unsubscribe();
	}, [getValues, setValue, watch]);

	useEffect(() => {
		if (companyOptions.length > 0) return;

		let isActive = true;
		getCompanies().then((companies) => {
			if (isActive && Array.isArray(companies)) {
				setCompanyOptions(companies);
			}
		});

		return () => {
			isActive = false;
		};
	}, []);

	useEffect(() => {
		if (companyOptions.length !== 1) return;

		schedulePeriods.forEach((period) => {
			if (!period.isNew || getValues(`${period.clientKey}.company`)) return;

			setValue(`${period.clientKey}.company`, companyOptions[0].value);
		});
	}, [companyOptions, getValues, schedulePeriods, setValue]);

	const formatDate = (value) => {
		if (!value) return '';
		const dateValue = String(value).split('T')[0];
		const isoDate = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateValue);
		if (isoDate) return `${isoDate[3]}/${isoDate[2]}/${isoDate[1]}`;
		const date = new Date(value);
		return Number.isNaN(date.getTime())
			? String(value)
			: `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
	};

	const getCompanyName = (companyCode) => (
		companyOptions.find((company) => company.value === companyCode)?.label ?? companyCode ?? ''
	);
	const getDepartmentName = (period, formKey) => (
		departmentOptionsByRow[formKey]?.find((department) => (
			String(department.value) === String(period.dept)
		))?.label ?? period.dept ?? ''
	);

	const handleDeleteSchedulePeriod = (item) => {
		if (item.isNew) {
			unregister(['dept', 'company', 'startDate', 'endDate'].map((field) => `${item.clientKey}.${field}`));
			setSchedulePeriods((periods) => periods.filter((period) => period.clientKey !== item.clientKey));
			setDepartmentOptionsByRow((options) => {
				const remaining = { ...options };
				delete remaining[item.clientKey];
				return remaining;
			});
			setDepartmentLoadingByRow((loading) => {
				const remaining = { ...loading };
				delete remaining[item.clientKey];
				return remaining;
			});
			if (activeCalendar?.clientKey === item.clientKey) setActiveCalendar(null);
			return;
		}

		if (item.isMarkedForDelete) {
			setSchedulePeriods((periods) => periods.map((period) => (
				period.id === item.id ? { ...period, isMarkedForDelete: false } : period
			)));
			return;
		}

		CustomAlert(
			t('common:delete'),
			t('common:deleteSchedulePeriodConfirmation'),
			[
				{ text: t('common:cancel'), style: 'cancel' },
				{
					text: t('common:delete'),
					style: 'destructive',
					onPress: () => {
						const formKey = getPeriodFormKey(item);
						['company', 'dept', 'startDate', 'endDate'].forEach((field) => {
							setValue(`${formKey}.${field}`, item[field] ?? '');
							clearErrors(`${formKey}.${field}`);
						});
						setSchedulePeriods((periods) => periods.map((period) => (
							period.id === item.id ? { ...period, isMarkedForDelete: true, isEditing: false } : period
						)));
					},
				},
			]
		);
	};

	const requestRosterPeriod = (periodId) => {
		if (periodId === selectedRosterPeriodId) return;
		if (!rosterHasPendingChanges) {
			setSelectedRosterPeriodId(periodId);
			return;
		}

		CustomAlert(
			t('common:unsavedRosterChangesTitle'),
			t('common:unsavedRosterChangesMessage'),
			[
				{ text: t('common:cancel'), style: 'cancel' },
				{ text: t('common:discardChanges'), style: 'destructive', onPress: () => setSelectedRosterPeriodId(periodId) },
			]
		);
	};

	const handleToggleDraftEdit = (item) => {
		if (item.isNew || item.status !== 'Draft' || item.isMarkedForDelete) return;

		const formKey = getPeriodFormKey(item);
		if (item.isEditing) {
			['company', 'dept', 'startDate', 'endDate'].forEach((field) => {
				setValue(`${formKey}.${field}`, item[field] ?? '');
				clearErrors(`${formKey}.${field}`);
			});
			if (activeCalendar?.clientKey === formKey) setActiveCalendar(null);
		}

		setSchedulePeriods((periods) => periods.map((period) => (
			period.id === item.id ? { ...period, isEditing: !item.isEditing } : period
		)));
	};

	const renderSchedulePeriod = ({ item }) => {
		const formKey = getPeriodFormKey(item);
		const isEditable = item.isNew || (item.status === 'Draft' && item.isEditing && !item.isMarkedForDelete);
		const isSelectedForRoster = String(item.id) === selectedRosterPeriodId;
		const canSelectRoster = Boolean(selectedRosterPeriod && !item.isNew && !item.isMarkedForDelete);
		const companyCode = isEditable ? watch(`${formKey}.company`) : '';
		return (
			<Card
				onPress={canSelectRoster ? () => requestRosterPeriod(String(item.id)) : undefined}
				style={[
					common.cardstyle,
					item.isMarkedForDelete && styles.deletedRow,
					isSelectedForRoster && styles.selectedRosterPeriod,
				]}
			>
				<Card.Content style={styles.row}>
					{isEditable ? (
						<>
							{!selectedRosterPeriod && (
								<View style={styles.item}>
									<CustomControlledDropdown
										control={control}
										name={`${formKey}.company`}
										rules={item.isMarkedForDelete ? {} : { required: t('common:companyReq') }}
										options={companyOptions}
										type="EDIT_EMPLOYEE"
										disabled={item.isMarkedForDelete}
									/>
								</View>
							)}
							<View style={styles.item}>
								<CustomControlledDropdown
									control={control}
									name={`${formKey}.dept`}
									rules={item.isMarkedForDelete ? {} : { required: t('common:deptReqMess') }}
									options={departmentOptionsByRow[formKey] || []}
									type="EDIT_EMPLOYEE"
									disabled={!companyCode || departmentLoadingByRow[formKey] || item.isMarkedForDelete}
								/>
							</View>
							{!selectedRosterPeriod && (
								<>
									<View style={styles.item}>
										<View>
											<View style={styles.calendarField}>
												<View style={styles.dateInput}>
													<CustomLabel value={formatDate(watch(`${formKey}.startDate`))} type="AVAILABLEUSED_SECONDARY" fontSize={16} />
												</View>
												<Ionicons name="calendar-outline" size={22} style={styles.calendarIcon} onPress={() => !item.isMarkedForDelete && toggleCalendar(formKey, 'startDate')} />
											</View>
											{errors[formKey]?.startDate?.message && (
												<CustomLabel value={errors[formKey].startDate.message} textStyle={styles.dateError} />
											)}
										</View>
									</View>
									<View style={styles.item}>
										<View>
											<View style={styles.calendarField}>
												<View style={styles.dateInput}>
													<CustomLabel value={formatDate(watch(`${formKey}.endDate`))} type="AVAILABLEUSED_SECONDARY" fontSize={16} />
												</View>
												<Ionicons name="calendar-outline" size={22} style={styles.calendarIcon} onPress={() => !item.isMarkedForDelete && toggleCalendar(formKey, 'endDate')} />
											</View>
											{errors[formKey]?.endDate?.message && (
												<CustomLabel value={errors[formKey].endDate.message} textStyle={styles.dateError} />
											)}
										</View>
									</View>
									<View style={styles.item}>
										<CustomLabel value={item.status} type="AVAILABLEUSED_SECONDARY" />
									</View>
								</>
							)}
						</>
					) : (
						<>
							{!selectedRosterPeriod && <View style={styles.item}><CustomLabel value={getCompanyName(item.company)} type="AVAILABLEUSED" fontSize={16} /></View>}
							<View style={styles.item}><CustomLabel value={getDepartmentName(item, formKey)} type="AVAILABLEUSED" fontSize={16} /></View>
							{!selectedRosterPeriod && (
								<>
									<View style={styles.item}><CustomLabel value={formatDate(item.startDate)} type="AVAILABLEUSED_SECONDARY" fontSize={16} /></View>
									<View style={styles.item}><CustomLabel value={formatDate(item.endDate)} type="AVAILABLEUSED_SECONDARY" fontSize={16} /></View>
									<View style={styles.item}><CustomLabel value={item.status || ''} type="AVAILABLEUSED_SECONDARY" fontSize={16} /></View>
								</>
							)}
						</>
					)}
					{!selectedRosterPeriod && <View style={styles.actionButtons}>
						<CustomButton
							iconName={item.isEditing ? 'close-outline' : 'pencil-outline'}
							onPress={() => handleToggleDraftEdit(item)}
							disabled={item.isNew || item.status !== 'Draft' || item.isMarkedForDelete}
							show="HOME_BELL"
							type="HOME_BELL"
							textTop="2"
							sz={20}
						/>
						{!item.isNew && !item.isEditing && !item.isMarkedForDelete && (
							<CustomButton
								iconName="calendar-outline"
								onPress={() => requestRosterPeriod(String(item.id))}
								show="HOME_BELL"
								type="HOME_BELL"
								textTop="2"
								sz={20}
							/>
						)}
						<CustomButton
							iconName={item.isMarkedForDelete ? 'refresh-outline' : 'trash-outline'}
							onPress={() => handleDeleteSchedulePeriod(item)}
							show="HOME_BELL"
							type="HOME_BELL"
							textTop="2"
							sz={20}
						/>
					</View>}
				</Card.Content>
				{isEditable && !item.isMarkedForDelete && activeCalendar?.clientKey === formKey && (
					<View style={styles.calendarContainer}>
						<CustomCalendar
							calendarType="RangePicker"
							setRange={handleCalendarChange}
							range={calendarRange}
							allowSingleDate
							startingYear={1950}
						/>
					</View>
				)}
			</Card>
		);
	};

	const getPeriodsToSave = (data) => schedulePeriods.filter((period) => {
		if (period.isMarkedForDelete) return false;
		if (period.isNew) return true;
		if (period.status !== 'Draft') return false;

		const values = data[getPeriodFormKey(period)] || {};
		return ['company', 'dept', 'startDate', 'endDate'].some((field) => (
			(values[field] ?? '') !== (period[field] ?? '')
		));
	});

	const validateDraftDates = (data) => {
		const periodsToSave = getPeriodsToSave(data);
		let hasDateErrors = false;

		periodsToSave.forEach((period) => {
			const values = data[getPeriodFormKey(period)] || {};
			['startDate', 'endDate'].forEach((field) => {
				if (!values[field]) {
					setError(`${getPeriodFormKey(period)}.${field}`, {
						type: 'required',
						message: t(`common:${field}Req`),
					});
					hasDateErrors = true;
				} else {
					clearErrors(`${getPeriodFormKey(period)}.${field}`);
				}
			});
		});

		return !hasDateErrors;
	};

	const onSubmit = async (data) => {
		if (!validateDraftDates(data)) return;

		const periodsToSave = getPeriodsToSave(data);

		for (const period of periodsToSave) {
			const formKey = getPeriodFormKey(period);
			const values = data[formKey] || {};
			const savedPeriod = await saveSchedulePeriod({
				id: period.isNew ? undefined : period.id,
				company: values.company || '',
				dept: values.dept || '',
				startDate: values.startDate,
				endDate: values.endDate,
				status: period.status || 'Draft',
			});

			if (!savedPeriod?.id) {
				showMessage({ message: t('common:saveError'), type: 'danger', duration: 3000 });
				return;
			}

			setSchedulePeriods((periods) => periods.map((current) => (
				getPeriodFormKey(current) === formKey
					? { ...current, ...values, id: savedPeriod.id, isNew: false, isEditing: false }
					: current
			)));
		}

		const periodsToDelete = schedulePeriods.filter((period) => period.isMarkedForDelete);
		for (const period of periodsToDelete) {
			const success = await deleteSchedulePeriod(period.id);
			if (!success) {
				showMessage({ message: t('common:deleteFailed'), type: 'danger', duration: 3000 });
				return;
			}

			setSchedulePeriods((periods) => periods.filter((current) => current.id !== period.id));
		}

		showMessage({ message: t('common:allOperationsSuccMess'), type: 'success', duration: 3000 });
	};

	const onInvalidSubmit = () => {
		validateDraftDates(getValues());
	};

	return (
		<View style={[styles.screenLayout, selectedRosterPeriod && (width > 992 ? styles.splitLayout : styles.stackedLayout)]}>
			<View
				accessible
				accessibilityLabel={t('navigate:shiftSchedulePeriods')}
				style={[common.container, styles.container, selectedRosterPeriod && (width > 992 ? styles.schedulePane : styles.stackedPane)]}
			>
				<View style={styles.header}>
					<CustomLabel
						value={t('navigate:shiftSchedulePeriods')}
						type="MINORTITLE"
						fontSize={20}
					/>
					<View style={common.titleButtonRow}>
						{hasChanges && (
							<CustomButton
								onPress={handleSubmit(onSubmit, onInvalidSubmit)}
								disabled={isLoading}
								text={t('common:save')}
								iconName='save-outline'
								show='BUTTON_SAVE'
								type='BUTTON_SAVE'
								isGradient={true}
								textTop={0}
								sz={15}
							/>
						)}
						{!selectedRosterPeriod && (
							<CustomButton
								text={t('common:new')}
								onPress={addSchedulePeriod}
								type='BUTTON_ADD'
								textTop='0'
								sz={15}
							/>
						)}
					</View>
				</View>
				<FlatList
					data={schedulePeriods}
					keyExtractor={(item, index) => String(item.id ?? item.clientKey ?? index)}
					renderItem={renderSchedulePeriod}
					ListHeaderComponent={(
						<View style={common.listTitle}>
							<View style={[styles.row, styles.headerRow]}>
								{!selectedRosterPeriod && <View style={styles.item}><CustomLabel value={t('common:company')} type="SUBTITLE" /></View>}
								<View style={styles.item}><CustomLabel value={t('common:department')} type="SUBTITLE" /></View>
								{!selectedRosterPeriod && (
									<>
										<View style={styles.item}><CustomLabel value={t('common:startDate')} type="SUBTITLE" /></View>
										<View style={styles.item}><CustomLabel value={t('common:endDate')} type="SUBTITLE" /></View>
										<View style={styles.item}><CustomLabel value={t('common:status')} type="SUBTITLE" /></View>
										<View style={styles.actionButtons} />
									</>
								)}
							</View>
							<View style={common.divider} />
						</View>
					)}
					refreshing={isLoading}
					onRefresh={loadSchedulePeriods}
					ListEmptyComponent={isLoading ? (
						<ActivityIndicator animating color={colors.blue} />
					) : (
						<CustomLabel value={t('common:noShiftSchedules')} type="NO_FUNCTIONS" />
					)}
				/>
			</View>
			{selectedRosterPeriod && (
				<View style={[styles.rosterPane, width <= 992 && styles.stackedPane]}>
					<View style={styles.rosterPaneHeader}>
						<CustomLabel
							value={`${getCompanyName(selectedRosterPeriod.company)} / ${getDepartmentName(selectedRosterPeriod, getPeriodFormKey(selectedRosterPeriod))}`}
							type="MINORTITLE"
							fontSize={16}
						/>
						<CustomButton
							iconName="close-outline"
							onPress={() => requestRosterPeriod(null)}
							show="HOME_BELL"
							type="HOME_BELL"
							textTop="2"
							sz={20}
						/>
					</View>
					<ShiftsRosterScreen
						schedulePeriodId={selectedRosterPeriod.id}
						company={selectedRosterPeriod.company}
						dept={selectedRosterPeriod.dept}
						onPendingChange={setRosterHasPendingChanges} />
				</View>
			)}
		</View>
	);
};

const makeStyles = (colors) => StyleSheet.create({
	container: {
		backgroundColor: colors.background,
	},
	screenLayout: {
		flex: 1,
		flexDirection: 'column',
	},
	splitLayout: {
		flexDirection: 'row',
	},
	stackedLayout: {
		flexDirection: 'column',
	},
	fullPane: {
		flex: 1,
		minWidth: 0,
	},
	rosterPane: {
		flex: 6,
		minWidth: 0,
		borderRightWidth: 1,
		borderColor: colors.border_card,
	},
	rosterPaneHeader: {
		minHeight: 44,
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		paddingHorizontal: 8,
	},
	schedulePane: {
		flex: 1,
		minWidth: 0,
	},
	stackedPane: {
		flex: 1,
		minHeight: 0,
		width: '100%',
	},
	row: {
		flexDirection: 'row',
		alignItems: 'center',
		marginHorizontal: 5,
		paddingVertical: 6,
	},
	headerRow: {
		paddingHorizontal: 16,
	},
	item: {
		flex: 1,
		minWidth: 0,
		marginHorizontal: 3,
	},
	deletedRow: {
		opacity: 0.45,
	},
	selectedRosterPeriod: {
		borderWidth: 2,
		borderColor: colors.green,
	},
	dateError: {
		color: 'red',
		fontSize: 12,
	},
	actionButtons: {
		width: 76,
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'flex-end',
	},
	calendarField: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	dateInput: {
		flex: 1,
		minWidth: 0,
	},
	calendarIcon: {
		marginHorizontal: 4,
		color: colors.text_primary,
	},
	calendarContainer: {
		alignItems: 'center',
		paddingHorizontal: 10,
	},
	header: {
		justifyContent: 'space-between',
		flexDirection: 'row',
		alignItems: 'center',
	},
});

export default ShiftsSchedulePeriodsScreen;